import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Plus, ChevronLeft, ChevronRight, Receipt, FileSpreadsheet } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { exportTaxMonthlyReport, lastMonthValue, LAB_STAFF_EXPORT_MSG } from '../utils/excelExport';

// 용어 통일 (2026-07-19): 작성 중 / 발행됨 — 명세서와 동일 팔레트
const STATUS_MAP = {
    draft: { label: '작성 중', bg: '#eef2ff', color: '#4338ca' },
    issued: { label: '발행됨', bg: '#fff7e8', color: '#b45309' },
    sent_to_nts: { label: '국세청 전송', bg: '#dbeafe', color: '#1d4ed8' },
    nts_confirmed: { label: '국세청 확인', bg: '#ede9fe', color: '#6d28d9' },
    cancelled: { label: '취소', bg: '#fee2e2', color: '#991b1b' },
};

const STATUS_OPTIONS = [
    { value: 'all', label: '전체 상태' },
    { value: 'draft', label: '작성 중' },
    { value: 'issued', label: '발행됨' },
    { value: 'sent_to_nts', label: '국세청 전송' },
    { value: 'nts_confirmed', label: '국세청 확인' },
    { value: 'cancelled', label: '취소' },
];

// 테스트 발행분 (팝빌 테스트 모드 — 국세청에 안 감). 법무 고정 문구 (계약 §2) — 글자 바꾸지 말 것
export const TEST_ISSUE_LABEL = '테스트 발행 — 국세청 미전송';
// 발행 완료(isElectronic 또는 electronicStatus 'issued')이면서 테스트일 때만 — 처리 중(processing)·오류(error)엔 붙이지 않는다
export const isTestIssuedInvoice = (inv) => !!inv
    && (inv.isElectronic === true || inv.electronicStatus === 'issued')
    && !['processing', 'error'].includes(inv.electronicStatus)
    && (inv.electronicMode === 'test' || inv.popbillIsTest === true);
// 원클릭 '덴클 기록' 모드 — 전자발행 안 함(electronicMode 없음/record, isElectronic false)
export const isOneClickRecordOnly = (inv) => !!inv && inv.source === 'oneclick' && inv.isElectronic !== true
    && (!inv.electronicMode || inv.electronicMode === 'record');

function StatusBadge({ status }) {
    const cfg = STATUS_MAP[status] || { label: status, bg: '#f1f5f9', color: '#475569' };
    return (
        <span style={{
            padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
            fontWeight: '600', background: cfg.bg, color: cfg.color,
        }}>
            {cfg.label}
        </span>
    );
}

const thStyle = {
    padding: '12px 10px', textAlign: 'left', fontSize: '12px',
    fontWeight: '700', color: '#64748b', whiteSpace: 'nowrap',
};
const tdStyle = {
    padding: '12px 10px', fontSize: '13px', color: '#334155',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

function TaxInvoiceList({ user, onSelectInvoice, onCreateNew }) {
    const [invoices, setInvoices] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedStatus, setSelectedStatus] = useState('all');

    const targetId = user?.companyId || user?.uid;
    // [세무 자료 내려받기] (2026-09-04) — 월 선택(기본 지난달), 거래명세서 목록과 같은 공용 함수
    const [taxMonth, setTaxMonth] = useState(() => lastMonthValue());
    const [exportingTax, setExportingTax] = useState(false);
    const handleExportTax = async () => {
        if (exportingTax) return;
        setExportingTax(true);
        try {
            const r = await exportTaxMonthlyReport(user, taxMonth);
            if (r.statementCount === 0 && r.invoiceCount === 0) {
                alert(`${taxMonth.replace('-', '년 ')}월에는 발행된 명세서·세금계산서가 없습니다.\n(작성 중인 문서는 포함되지 않습니다)`);
            } else {
                alert(`엑셀 파일을 내려받았습니다.\n${r.fileName}\n\n거래명세서 ${r.statementCount}건 · 세금계산서 ${r.invoiceCount}건`);
            }
        } catch (e) {
            if (e?.message === LAB_STAFF_EXPORT_MSG) { alert(LAB_STAFF_EXPORT_MSG); return; }   // 권한 안내(오류 아님)
            console.error('세무 자료 내려받기 실패:', e);
            alert(`세무 자료를 만들지 못했습니다.\n${e?.message || e?.code || ''}\n잠시 후 다시 시도해주세요.`);
        } finally {
            setExportingTax(false);
        }
    };

    useEffect(() => {
        if (user) fetchInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        applyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoices, selectedYear, selectedMonth, selectedStatus]);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const col = collection(db, 'taxInvoices');
            const currentUid = auth.currentUser?.uid;

            // targetId와 uid 둘 다 조회
            const ids = new Set([targetId, currentUid].filter(Boolean));
            const all = [];
            const idSet = new Set();

            for (const id of ids) {
                try {
                    const issuedSnap = await getDocs(query(col, where('createdBy', '==', id)));
                    issuedSnap.docs.forEach(d => {
                        if (!idSet.has(d.id)) {
                            idSet.add(d.id);
                            all.push({ id: d.id, ...d.data(), direction: 'issued' });
                        }
                    });
                } catch (e) { /* skip */ }

                try {
                    const receivedSnap = await getDocs(query(col, where('buyerInfo.userId', '==', id)));
                    receivedSnap.docs.forEach(d => {
                        // 상대방의 작성 중(draft) 문서는 수신 목록에서 제외 — "발행 전에는 보이지 않습니다" 보장
                        if (!idSet.has(d.id) && d.data().status !== 'draft') {
                            idSet.add(d.id);
                            all.push({ id: d.id, ...d.data(), direction: 'received' });
                        }
                    });
                } catch (e) { /* skip */ }
            }

            all.sort((a, b) => {
                const at = a.createdAt?.toDate?.() || new Date(0);
                const bt = b.createdAt?.toDate?.() || new Date(0);
                return bt - at;
            });

            setInvoices(all);
        } catch (e) {
            console.error('세금계산서 로드 실패:', e);
        }
        setLoading(false);
    };

    const applyFilters = () => {
        let result = [...invoices];

        result = result.filter(inv => {
            const d = inv.issueDate
                ? new Date(inv.issueDate)
                : inv.createdAt?.toDate?.() || new Date(0);
            return d.getFullYear() === selectedYear && d.getMonth() + 1 === selectedMonth;
        });

        if (selectedStatus !== 'all') {
            result = result.filter(inv => inv.status === selectedStatus);
        }

        setFiltered(result);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };

    const formatCurrency = (v) => (v || 0).toLocaleString();

    const changeMonth = (delta) => {
        let m = selectedMonth + delta;
        let y = selectedYear;
        if (m < 1) { m = 12; y -= 1; }
        if (m > 12) { m = 1; y += 1; }
        setSelectedMonth(m);
        setSelectedYear(y);
    };

    const issuedList = filtered.filter(i => i.direction === 'issued');
    const receivedList = filtered.filter(i => i.direction === 'received');
    const issuedTotal = issuedList.reduce((s, i) => s + (i.totalAmount || 0), 0);
    const receivedTotal = receivedList.reduce((s, i) => s + (i.totalAmount || 0), 0);

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* 페이지 제목 + 상단 버튼 (2026-08-18 디자인 통일 — 제목 신설, 버튼 기준 규격) */}
            <PageHeader
                icon={Receipt}
                title="세금계산서"
                actions={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {/* 세무사에게 보낼 월별 자료 — 명세서·계산서 엑셀 1개(시트 3장), 환자 정보 없음 */}
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>세무사에게 보낼 월별 자료</span>
                    <input
                        type="month"
                        value={taxMonth}
                        onChange={(e) => setTaxMonth(e.target.value)}
                        style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
                    />
                    <button
                        onClick={handleExportTax}
                        disabled={exportingTax || !taxMonth}
                        title="선택한 달의 거래명세서·세금계산서를 엑셀 파일 1개(시트 3장)로 내려받습니다"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', background: exportingTax ? '#e2e8f0' : '#f1f5f9', color: '#475569',
                            border: '1px solid #e2e8f0', borderRadius: '8px', cursor: exportingTax ? 'wait' : 'pointer',
                            fontWeight: '600', fontSize: '13px',
                        }}
                    >
                        <FileSpreadsheet size={15} /> {exportingTax ? '만드는 중…' : '세무 자료 내려받기'}
                    </button>
                    {onCreateNew && (
                    <button
                        onClick={onCreateNew}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 16px', background: '#6366f1', color: 'white',
                            border: 'none', borderRadius: '8px', cursor: 'pointer',
                            fontWeight: '600', fontSize: '13px',
                        }}
                    >
                        <Plus size={16} /> 전자세금계산서 작성
                    </button>
                    )}
                    </div>
                )}
            />
            <div style={{ marginBottom: '20px' }} />

            {/* 필터 */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button
                        onClick={() => changeMonth(-1)}
                        style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: 'pointer' }}
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <span style={{ fontWeight: '600', fontSize: '15px', minWidth: '100px', textAlign: 'center' }}>
                        {selectedYear}년 {selectedMonth}월
                    </span>
                    <button
                        onClick={() => changeMonth(1)}
                        style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: 'pointer' }}
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>

                <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px' }}
                >
                    {STATUS_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>

            {/* 요약 카드 */}
            {!loading && filtered.length > 0 && (
                <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    {/* 2026-08-18 디자인 통일 — 그라데이션 제거, 연보라 면+보더(실시간 매출 총합 카드 문법) */}
                    <div style={{
                        flex: '1', minWidth: '200px',
                        background: '#eef2ff', border: '1px solid #c7d2fe',
                        borderRadius: '12px', padding: '16px',
                    }}>
                        <div style={{ fontSize: '12px', color: '#4338ca', marginBottom: '4px' }}>{selectedYear}년 {selectedMonth}월 합계</div>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: '#312e81' }}>{formatCurrency(issuedTotal + receivedTotal)}원</div>
                        <div style={{ fontSize: '12px', color: '#6366f1', marginTop: '4px' }}>총 {filtered.length}건</div>
                    </div>
                    <div style={{ flex: '1', minWidth: '200px', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>발행 합계</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#4338ca' }}>{formatCurrency(issuedTotal)}원</div>
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>{issuedList.length}건</div>
                    </div>
                    <div style={{ flex: '1', minWidth: '200px', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>수신 합계</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#c2410c' }}>{formatCurrency(receivedTotal)}원</div>
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>{receivedList.length}건</div>
                    </div>
                </div>
            )}

            {/* 테이블 목록 */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>로딩 중...</div>
            ) : filtered.length === 0 ? (
                <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: '14px' }}>
                    <Receipt size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
                    {['dental', 'clinic'].includes(user?.businessType || user?.companyBusinessType || '') && invoices.length === 0 ? (
                        <p style={{ lineHeight: 1.7 }}>
                            {'거래 기공소가 발행한 명세서(계산서)가 이곳에 도착합니다.'}<br />
                            {'아직 받은 문서가 없습니다.'}
                        </p>
                    ) : (
                        <>
                            <p>해당 기간의 전자세금계산서가 없습니다.</p>
                            <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '6px' }}>[거래명세서] 목록에서 발행된 명세서를 열어 [세금계산서 발행]을 누르면 여기에 표시됩니다.</p>
                        </>
                    )}
                </div>
            ) : (
                // 모바일: 열 압착 대신 가로 스크롤 (overflowX auto + 테이블 minWidth)
                <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflowX: 'auto' }}>
                    <table style={{ width: '100%', minWidth: '960px', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <colgroup>
                            <col style={{ width: '6%' }} />
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '11%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '11%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                        </colgroup>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                <th style={thStyle}>구분</th>
                                <th style={thStyle}>전자</th>
                                <th style={thStyle}>계산서번호</th>
                                <th style={thStyle}>거래처</th>
                                <th style={thStyle}>공급가</th>
                                <th style={thStyle}>세액</th>
                                <th style={thStyle}>합계 금액</th>
                                <th style={thStyle}>발행일</th>
                                <th style={thStyle}>작성일</th>
                                <th style={thStyle}>상태</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((inv, index) => {
                                const partnerName = inv.direction === 'issued'
                                    ? inv.buyerInfo?.name || '-'
                                    : inv.supplierInfo?.name || '-';
                                return (
                                    <tr
                                        key={inv.id}
                                        onClick={() => onSelectInvoice && onSelectInvoice(inv.id)}
                                        style={{
                                            borderBottom: '1px solid #f1f5f9',
                                            background: index % 2 === 1 ? '#f8fafc' : '#fff',
                                            cursor: 'pointer',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#eef2ff'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 1 ? '#f8fafc' : '#fff'}
                                    >
                                        {/* 구분 */}
                                        <td style={tdStyle}>
                                            <span style={{
                                                padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                                                background: inv.direction === 'issued' ? '#eef2ff' : '#fff7ed',
                                                color: inv.direction === 'issued' ? '#4338ca' : '#c2410c',
                                            }}>
                                                {inv.direction === 'issued' ? '발행' : '수신'}
                                            </span>
                                        </td>
                                        {/* 전자 여부 */}
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {inv.isElectronic ? (
                                                <span style={{
                                                    padding: '3px 6px', borderRadius: '6px', fontSize: '10px', fontWeight: '700',
                                                    background: '#dbeafe', color: '#1d4ed8',
                                                }}>전자</span>
                                            ) : (
                                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>전자 아님</span>
                                            )}
                                            {/* 명세서 원클릭 발행분 (서버 생성, 2026-10-24 2단계 ⑤) */}
                                            {inv.source === 'oneclick' && (
                                                <div style={{ marginTop: 3 }}>
                                                    <span style={{ padding: '2px 6px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', background: '#ecfdf5', color: '#047857' }}>{'명세서에서 바로 발행'}</span>
                                                </div>
                                            )}
                                            {isOneClickRecordOnly(inv) && (
                                                <div style={{ marginTop: 3 }}>
                                                    <span style={{ padding: '2px 6px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', background: '#f1f5f9', color: '#475569' }}>{'덴클 기록만 (국세청 미전송)'}</span>
                                                </div>
                                            )}
                                        </td>
                                        {/* 계산서번호 */}
                                        <td style={{ ...tdStyle, color: '#6366f1', fontWeight: '600' }}>
                                            {inv.invoiceNumber || '-'}
                                        </td>
                                        {/* 거래처 */}
                                        <td style={{ ...tdStyle, fontWeight: '600', color: '#0f172a' }}>
                                            {partnerName}
                                        </td>
                                        {/* 공급가 */}
                                        <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b' }}>
                                            {formatCurrency(inv.supplyAmount)}원
                                        </td>
                                        {/* 세액 */}
                                        <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b' }}>
                                            {formatCurrency(inv.taxAmount)}원
                                        </td>
                                        {/* 합계 금액 */}
                                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>
                                            {formatCurrency(inv.totalAmount)}원
                                        </td>
                                        {/* 발행일 (issueDate) */}
                                        <td style={{ ...tdStyle, color: '#64748b', textAlign: 'center' }}>
                                            {formatDate(inv.issueDate)}
                                        </td>
                                        {/* 작성일 (createdAt) */}
                                        <td style={{ ...tdStyle, color: '#64748b', textAlign: 'center' }}>
                                            {inv.createdAt?.toDate
                                                ? formatDate(inv.createdAt.toDate().toISOString())
                                                : formatDate(inv.createdAt)}
                                        </td>
                                        {/* 상태 */}
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <StatusBadge status={inv.status} />
                                            {isTestIssuedInvoice(inv) && (
                                                <div style={{ marginTop: 3, fontSize: '10.5px', fontWeight: '700', color: '#92400e' }}>{TEST_ISSUE_LABEL}</div>
                                            )}
                                            {/* 수신처 확인 상태 — 치과 확인 작업 표시 */}
                                            {inv.status === 'issued' && inv.buyerStatus === 'confirmed' && (
                                                <span style={{ display: 'inline-block', marginTop: 3, padding: '2px 7px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', background: '#d1fae5', color: '#065f46' }}>확인 완료</span>
                                            )}
                                            {inv.status === 'issued' && inv.buyerStatus === 'revision_requested' && (
                                                <span style={{ display: 'inline-block', marginTop: 3, padding: '2px 7px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', background: '#fef2f2', color: '#dc2626' }}>수정 요청 중</span>
                                            )}
                                            {inv.status === 'issued' && !inv.buyerStatus && inv.direction === 'received' && (
                                                <span style={{ display: 'inline-block', marginTop: 3, padding: '2px 7px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', background: '#fef3c7', color: '#92400e' }}>확인 대기</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default TaxInvoiceList;
